'use client';

import { Facebook, Twitter, Linkedin, Share2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import PreferredSourceButton from '@/components/resources/PreferredSourceButton';

interface ResourceShareButtonsProps {
  title: string;
  url: string;
  description?: string;
}

export default function ResourceShareButtons({ title, url, description }: ResourceShareButtonsProps) {
  const [currentUrl, setCurrentUrl] = useState(url);

  useEffect(() => {
    // Use current page URL if available, otherwise use provided URL
    if (typeof window !== 'undefined') {
      setCurrentUrl(window.location.href);
    }
  }, []);
  const [copied, setCopied] = useState(false);

  const shareText = description || title;
  const encodedUrl = encodeURIComponent(currentUrl);
  const encodedTitle = encodeURIComponent(title);
  const encodedText = encodeURIComponent(shareText);

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
  };

  const handleShare = async (platform: 'facebook' | 'twitter' | 'linkedin' | 'copy') => {
    if (platform === 'copy') {
      try {
        await navigator.clipboard.writeText(currentUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    } else {
      window.open(shareLinks[platform], '_blank', 'width=600,height=400');
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground hidden sm:inline">Share:</span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => handleShare('facebook')}
            className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-blue-600"
            title="Share on Facebook"
            aria-label="Share on Facebook"
          >
            <Facebook className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleShare('twitter')}
            className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-blue-400"
            title="Share on X (Twitter)"
            aria-label="Share on X"
          >
            <Twitter className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleShare('linkedin')}
            className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-blue-700"
            title="Share on LinkedIn"
            aria-label="Share on LinkedIn"
          >
            <Linkedin className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleShare('copy')}
            className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
            title="Copy link"
            aria-label="Copy link"
          >
            <Share2 className="w-4 h-4" />
          </button>
        </div>
        {copied && (
          <span className="text-xs text-muted-foreground animate-in fade-in duration-200">
            Copied!
          </span>
        )}
      </div>
      <PreferredSourceButton />
    </div>
  );
}
